//@flow
import * as React from 'react';
import {connect} from "react-redux";
import NCard from "../../../../../../../components/simple/Containers/NCard/NCard";
import type {
  actTemplateId, contractId,
  fileName, groupFinTermId, isScrollToError,
  itemDate, reportFileNamePattern, validationIsAllowed
} from "../../../../../../../services/flowTypes/dataFlowTypes";
import Text1 from "../../../../../../../components/simple/TextComponents/Text1/Text1";
import text from "../../../../../../../services/localization/text";
import BtnEditAct from "../../../../../../../components/simple/Buttons/BtnEditAct/BtnEditAct";
import boldWhiteArrow from '../../../../../../../assets/bold-white-arrow.svg';
import actTemplatesEditActions
  from "../../../../../../../store/organization/item/actTemplatesEdit/actTemplatesEditActions";
import {
  onGetActTemplateProp,
  onGetValidationStateFormProp
} from "../../../../../../../services/flowTypes/propFnFlowTypes";
import NFormGroup from "../../../../../../../components/simple/FormComponents/NFormGroup/NFormGroup";
import validationActions from "../../../../../../../store/validation/validationActions";
import {actTemplatesEditPageIds} from "../../../../../../../tests/testIds";
import InputFile from "../../../../../../../components/simple/FormComponents/InputFile/InputFile";
import type {onAddAlert, onGetValidationStateProp} from "../../../../../../../services/flowTypes/propFnFlowTypes";
import FileWithTags from "./FileWithTags";
import alertsActions from "../../../../../../../store/alerts/alertsActions";
import alertsList from "../../../../../../../services/alertsList/alertsList";
import ReportFileNamePattern from "./ReportFileNamePattern";
import apiErrorHandler from "../../../../../../../services/apiInstance/apiErrorHandler";
import api from "../../../../../../../services/apiInstance/api";
import RenderOnReady from "../../../../../../../components/service/RenderOnReady/RenderOnReady";
import type {errorObject, isReadyToRender} from "../../../../../../../services/flowTypes/appFlowTypes";
import {defineWrongFileName} from "../../../../../../../components/composed/ItemDocuments/DocumentUploader/DocumentUploaderContent";
import AlertOnWrongFileName from "../../../../../../../components/composed/Alerts/AlertOnWrongFileName";
import DocumentFunctional
  from "../../../../../../../components/composed/ItemDocuments/DocumentEdit/Document/DocumentFunctional";
import TemplateList from "../../../../../../../components/composed/ItemDocuments/TemplateList";

type Props = {
  fileName: fileName,
  organizationId: organizationId,
  documentDate: itemDate,
  actTemplateId: actTemplateId,
  tempFileId: string,
  globalTemplateFile: boolean,
  globalTemplateFileId: string,
  globalTemplateFileName: string,
  onGetActTemplateProp: onGetActTemplateProp,
  onGetValidationStateFormProp: onGetValidationStateFormProp,
  validationForm: Object,
  isScrollToError: isScrollToError,
  validationIsAllowed: validationIsAllowed,
  readonly?: boolean,
  onGetValidationStateProp: onGetValidationStateProp,
  contractId: contractId,
  finTermGroupIds: Array<groupFinTermId>,
  onAddAlert: onAddAlert,
  reportFileNamePattern: reportFileNamePattern
};
type State = {
  errorObject: errorObject,
  isReadyToRender: isReadyToRender,
  wrongFileName: fileName
};


class ActTemplateDocument extends React.Component <Props, State> {
  inputFile: { current: null | HTMLInputElement };
  
  // Константы
  static ALLOWED_EXTENSIONS = ['docx', 'txt', 'xlsx'];
  static FILE_EXTENSION_REGEX = /(?:\.([^.]+))?$/;

  constructor(props: Props) {
    super(props);
    this.state = {
      errorObject: null,
      isReadyToRender: true,
      wrongFileName: ''
    };
    this.inputFile = React.createRef();
  }

  // Получить расширение файла из имени
  getFileExtension = (fileName) => {
    if (!fileName) return null;
    const match = ActTemplateDocument.FILE_EXTENSION_REGEX.exec(fileName);
    return match && match[1] ? match[1].toLowerCase() : null;
  };

  // Получить сообщение об ошибке для формата файла
  getFormatFileErrorStr = (ext) => {
    const errorMap = {
      'doc': text.errorFileFormat.oldDoc,
      'xls': text.errorFileFormat.oldXls
    };
    return errorMap[ext] || text.errorFileFormat.unknownFileFormat;
  };

  // Проверить формат файла
  validateFileFormat = (fileName) => {
    if (!fileName) return true;
    const ext = this.getFileExtension(fileName);
    return ext ? ActTemplateDocument.ALLOWED_EXTENSIONS.includes(ext) : false;
  };

  // Очистить поля шаблона
  clearTemplateFields = (isGlobal) => {
    if (isGlobal) {
      this.props.onGetActTemplateProp('globalTemplateFile', false);
      this.props.onGetActTemplateProp('globalTemplateFileId', '');
      this.props.onGetActTemplateProp('globalTemplateFileName', '');
      this.props.onGetValidationStateFormProp('globalTemplateFileId', '');
    } else {
      this.props.onGetActTemplateProp('tempFileId', '');
      this.props.onGetActTemplateProp('fileName', '');
      this.props.onGetValidationStateFormProp('tempFileId', '');
    }
    this.props.onGetValidationStateFormProp('fileName', '');
  };

  // Обработчик выбора шаблона из списка
  handleTemplateSelect = (template) => {
    this.clearTemplateFields(false);
    
    this.props.onGetActTemplateProp('globalTemplateFile', true);
    this.props.onGetActTemplateProp('globalTemplateFileId', template.id);
    this.props.onGetActTemplateProp('globalTemplateFileName', template.name);
    this.props.onGetValidationStateFormProp('globalTemplateFileId', '');
    
    if (!this.validateFileFormat(template.name)) {
      this.props.onGetValidationStateFormProp('fileName', text.error.unsupportedFileFormat);
    }
    
    this.props.onAddAlert(alertsList.onActTemplateDocument);
  };

  uploadDoc = () => {
    const input = this.inputFile.current;
    if (!input || !input.files || !input.files[0]) return;

    this.setState({wrongFileName: ''});
    const file = input.files[0];
    const fileName = file.name;
    
    const wrongFileName = defineWrongFileName(fileName);
    if (wrongFileName) {
      this.setState({wrongFileName});
      return;
    }
    
    this.clearTemplateFields(true);
    
    const ext = this.getFileExtension(fileName);
    if (ext && !ActTemplateDocument.ALLOWED_EXTENSIONS.includes(ext)) {
      this.props.onGetValidationStateFormProp('tempFileId', this.getFormatFileErrorStr(ext));
      this.props.onGetValidationStateProp('validationIsAllowed', true);
      this.props.onGetActTemplateProp('fileName', fileName);
      this.props.onGetActTemplateProp('tempFileId', '');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    this.setState({isReadyToRender: false});
    
    api.uploadTempFile(formData)
      .then(response => {
        this.props.onGetActTemplateProp('tempFileId', response.data.id);
        this.props.onGetActTemplateProp('fileName', fileName);
        this.props.onGetValidationStateFormProp('tempFileId', '');
        this.props.onAddAlert(alertsList.onActTemplateDocument);
        this.setState({isReadyToRender: true});
      })
      .catch(error => {
        this.setState({
          isReadyToRender: true,
          errorObject: apiErrorHandler(error)
        });
      });
  };
  // Получить имя файла для отображения
  getDisplayFileName = () => {
    if (this.props.globalTemplateFile) {
      return this.props.globalTemplateFileName;
    }
    return this.props.fileName || 'Файл не выбран';
  };

  componentDidMount(){
    if (this.props.actTemplateId) return;
    
    // Проверяем наличие файла
    if (!this.props.tempFileId && !this.props.globalTemplateFileId) {
      this.props.onGetValidationStateFormProp('tempFileId', text.error.actTemplateFileRequired);
    }
    
    // Проверяем формат файла
    const fileName = this.getDisplayFileName();
    if (fileName && fileName !== 'Файл не выбран' && !this.validateFileFormat(fileName)) {
      this.props.onGetValidationStateFormProp('fileName', text.error.unsupportedFileFormat);
    }
  }

  // Получить параметры документа для DocumentFunctional
  getDocumentConfig = () => {
    const isGlobal = this.props.globalTemplateFile;
    const documentName = this.getDisplayFileName();
    
    return {
      document: {
        name: documentName,
        fileDescription: '',
        createDate: this.props.documentDate,
        fileId: isGlobal ? this.props.globalTemplateFileId : undefined
      },
      itemType: isGlobal ? 'organizations' : 'actTemplate',
      itemId: isGlobal ? this.props.organizationId : this.props.actTemplateId,
      actionName: isGlobal ? 'downloadDocument' : 'downloadFile',
      isGlobalTemplate: isGlobal
    };
  };

  // Получить ошибки валидации
  getValidationError = () => {
    if (!this.props.validationIsAllowed) return '';
    
    const {validationForm} = this.props;
    return validationForm.tempFileId || 
           validationForm.globalTemplateFileId || 
           validationForm.fileName || 
           '';
  };

  render() {
    const {actTemplateId, readonly, organizationId, contractId, finTermGroupIds} = this.props;
    const documentConfig = this.getDocumentConfig();
    const propError = this.getValidationError();
    // Компонент загрузки файла
    const fileUploadInput = (
      <InputFile 
        id={actTemplatesEditPageIds.buttonUploadDocument}
        onInputFileChange={this.uploadDoc}
        ref={this.inputFile}
        label={
          <div className="BtnNewItem pl-pr_2du">
            <img className={"ButtonNewItem__arrow"} src={boldWhiteArrow} alt="arrow-icon"/>
            {text.uploadActTemplateDoc}
          </div>
        }
      />
    );

    // Компонент замены файла
    const fileReplaceInput = (
      <InputFile 
        id={actTemplatesEditPageIds.buttonUploadDocument}
        onInputFileChange={this.uploadDoc}
        ref={this.inputFile}
        label={<div className="BtnEditAct">{text.substituteDocument}</div>}
      />
    );

    const documentUploader = (
      <NFormGroup propError={propError} isScrollToError={this.props.isScrollToError}>
        <div className={'flex'}>
          <AlertOnWrongFileName wrongFileName={this.state.wrongFileName}/>
          {fileUploadInput}
          <BtnEditAct addClasses={'ml_3du'} onClick={null}>
            {documentConfig.document.name}
          </BtnEditAct>
        </div>
        <TemplateList
          organizationId={organizationId}
          onTemplateClick={this.handleTemplateSelect}
        />
      </NFormGroup>
    );

    const documentBtns = readonly ? null : (
      <div className="flex jc_fe">
        <AlertOnWrongFileName wrongFileName={this.state.wrongFileName}/>
        {fileReplaceInput}
      </div>
    );

    const reportFileNamePattern = this.props.reportFileNamePattern?.length 
      ? this.props.reportFileNamePattern 
      : 'N/A';
    return (
      <NCard addClasses={'mb_3du'}>
        <RenderOnReady 
          isReadyToRender={this.state.isReadyToRender} 
          errorObject={this.state.errorObject}
        />
        <Text1 addClasses={'mb_1du'}>
          {text.uploadActTemplateFile1} <b>{text.uploadActTemplateFile2}</b>
          {text.uploadActTemplateFile3}<b>{text.uploadActTemplateFile4}</b>{'.'}
        </Text1>
        <FileWithTags contractId={contractId} finTermGroupIds={finTermGroupIds} />
        <ReportFileNamePattern 
          reportFileNamePatternValue={reportFileNamePattern} 
          readonly={readonly}
        />
        {actTemplateId ? (
          <>
            <NFormGroup propError={propError} isScrollToError={this.props.isScrollToError}>
              <DocumentFunctional
                document={documentConfig.document}
                itemId={documentConfig.itemId}
                itemType={documentConfig.itemType}
                actionName={documentConfig.actionName}
                documentBtns={documentBtns}
                linkIdToDownload={actTemplatesEditPageIds.buttonDownloadDocument}
                isGlobalTemplate={documentConfig.isGlobalTemplate}
              />
            </NFormGroup>
            {!readonly && (
              <TemplateList
                organizationId={organizationId}
                onTemplateClick={this.handleTemplateSelect}
              />
            )}
          </>
        ) : (
          documentUploader
        )}
      </NCard>
    );
  }
}

const mapStateToProps = state => {
  return {
    organizationId: state.organization.organizationId,
    fileName: state.organization.item.actTemplatesEdit.fileName,
    documentDate: state.organization.item.actTemplatesEdit.documentDate,
    finTermGroupIds: state.organization.item.actTemplatesEdit.finTermGroupIds,
    tempFileId: state.organization.item.actTemplatesEdit.tempFileId,
    globalTemplateFile: state.organization.item.actTemplatesEdit.globalTemplateFile,
    globalTemplateFileId: state.organization.item.actTemplatesEdit.globalTemplateFileId,
    globalTemplateFileName: state.organization.item.actTemplatesEdit.globalTemplateFileName,
    validationForm: state.validation.form,
    isScrollToError: state.validation.isScrollToError,
    validationIsAllowed: state.validation.validationIsAllowed,
    reportFileNamePattern: state.organization.item.actTemplatesEdit.reportFileNamePattern
  };
};

const mapDispatchToProps = dispatch => {
  const createAction = (type, value) => dispatch({type, value});
  
  return {
    onGetActTemplateProp: (prop, data) => createAction(
      actTemplatesEditActions.ACT_TEMPLATES_PROP_SET, 
      {prop, data}
    ),
    onGetValidationStateFormProp: (prop, data) => createAction(
      validationActions.VALIDATION_STATE_FORM_PROP_SET, 
      {prop, data}
    ),
    onGetValidationStateProp: (prop, data) => createAction(
      validationActions.VALIDATION_STATE_PROP_SET, 
      {prop, data}
    ),
    onAddAlert: (newAlert) => createAction(alertsActions.ALERT_ADD, newAlert)
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ActTemplateDocument);
